import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {useAiCoach} from "@/hooks/useAiCoach";
import { aiCoachQuickPrompts, assistantAvatar, type AiCoachProfile } from "@/mocks/aiCoach";
import type { Plan } from "@/data/plans";
import { usePlanCatalog } from "@/hooks/usePlanCatalog";
import MarkdownText from "./MarkdownText";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  image?: string;
}

// Paket bilgisi tek kanonik kaynaktan (src/data/plans.ts) üretilir.
function buildPackageList(plans: Plan[]): string {
  return plans
    .map((p) => {
      if (p.id === "ucretsiz") return `**${p.name}** (temel web içerikleri; Meraklı ve Toplantı dahil değil)`;
      const highlights = p.features.slice(0, 3).join(", ");
      return `**${p.name}** (aylık ${p.price}; ${highlights})`;
    })
    .join(", ");
}

function firstWord(name: string): string {
  return name.trim().split(" ")[0];
}

// Türkçe ünlü uyumuna göre özel isimlere ek ekler (kesme işaretiyle).
// "YÜKSEL" → "YÜKSEL'in", "YÜKSEL" → "YÜKSEL'e" gibi.
function trLastVowel(word: string): string {
  const vowels = "aıeioöuü";
  const lower = word.toLocaleLowerCase("tr-TR");
  for (let i = lower.length - 1; i >= 0; i--) {
    if (vowels.includes(lower[i])) return lower[i];
  }
  return "e";
}

function trGenitive(word: string): string {
  if (!word) return word;
  const v = trLastVowel(word);
  const suffix = "aı".includes(v) ? "ın" : "ei".includes(v) ? "in" : "ou".includes(v) ? "un" : "ün";
  return `${word}'${suffix}`;
}

function trDative(word: string): string {
  if (!word) return word;
  const v = trLastVowel(word);
  const suffix = "aıou".includes(v) ? "a" : "e";
  return `${word}'${suffix}`;
}

// "Thinking" modelinin iç monoloğunu cevaba karıştırıp karıştırmadığını tespit eder.
// İngilizce iç düşünce kalıpları birden fazla geçiyorsa bu bir akıl yürütme sızıntısıdır;
// o zaman gerçek cevap yerine yerel mantığa düşeriz.
function looksLikeThinkingLeak(text: string): boolean {
  const t = (text || "").toLocaleLowerCase("en-US");
  if (!t.trim()) return false;
  const markers = [
    "okay,", "let me", "wait,", "first, i need", "the user is asking",
    "let me check", "i need to check", "the system message", "my response should",
    "so my answer", "i should ask", "i'll check", "let's see", "i need to",
    "the system says", "according to the system", "let me look", "i think",
  ];
  const hits = markers.filter((m) => t.includes(m)).length;
  return hits >= 2;
}

function buildGreeting(profile: AiCoachProfile, siteName: string): string {
  const name = firstWord(profile.name);

  if (profile.role === "ogrenci") {
    const grade = profile.grade ? ` ${profile.grade} öğrencisi olarak` : "";
    return `Merhaba ${name}! Ben ${siteName} Asistanıyım.${grade} derslerin, soru çözümün, deneme stratejin ve motivasyonun için yanındayım. Aklına ne gelirse sorabilirsin.`;
  }
  if (profile.role === "veli") {
    const child = profile.childName ? `${trGenitive(profile.childName)}` : "Çocuğunuzun";
    return `Merhaba ${name}! Ben ${siteName} Asistanıyım. ${child} gelişimi, ders durumu, raporları ve evde nasıl destek olacağınız hakkında bana sorabilirsiniz.`;
  }
  if (profile.role === "ogretmen") {
    const ders = profile.subjects.length ? ` ${profile.subjects.join(" ve ")} derslerinizde` : " derslerinizde";
    return `Merhaba ${name} Hocam! Ben ${siteName} Asistanıyım.${ders} sınıf analizi, ödev ve test oluşturma, öğrenci takibi konularında yanınızdayım.`;
  }
  if (profile.role === "guest") {
    return `Merhaba! Ben ${siteName} Asistanıyım. Platformumuzda video dersler, canlı dersler, soru bankası ve deneme sınavları bir arada. Size nasıl yardımcı olabilirim?`;
  }
  return `Merhaba ${name}! Ben ${siteName} Asistanıyım. ${profile.roleLabel} olarak platformu yönetirken size yardımcı olmak için buradayım. Kullanıcılar, içerikler, paketler veya sistem ayarları hakkında sorabilirsiniz.`;
}

// Kişisel bağlama göre cevap üreten yerel yedek (fallback) mantığı.
// Edge Function yanıt veremediğinde bu mantık devreye girer.
function contextLabel(profile: AiCoachProfile): string {
  const parts: string[] = [profile.name];
  if (profile.role === "ogrenci") {
    if (profile.grade) parts.push(profile.grade);
    if (profile.exam) parts.push(profile.exam);
    if (profile.city) parts.push(profile.city);
  } else if (profile.role === "veli") {
    if (profile.childName) parts.push(profile.childName);
    if (profile.childGrade) parts.push(profile.childGrade);
    if (profile.exam) parts.push(profile.exam);
  } else if (profile.role === "ogretmen") {
    if (profile.subjects.length) parts.push(profile.subjects.join(", "));
  }
  return parts.join(" · ");
}

export default function AiCoachDrawer({ profile, siteName }: { profile: AiCoachProfile; siteName: string }) {
  const { plans } = usePlanCatalog();
  const { open, closeCoach, pendingPrompt, clearPrompt } = useAiCoach();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    { id: "greeting", role: "assistant", text: buildGreeting(profile, siteName) },
  ]);
  const [input, setInput] = useState("");
  const [image, setImage] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);
  const [guestName,setGuestName]=useState("");
  const [guestPhone,setGuestPhone]=useState("");
  const [logError,setLogError]=useState("");
  const logSession=useRef<{id:string;token:string}|null>(null);
  const pendingLogs=useRef<Record<string,unknown>[]>([]);
  const archive = async (message:Record<string,unknown>, session=logSession.current) => {
    const queue=pendingLogs.current;
    queue.push(message);
    while(queue.length){
      const r=await fetch('/api/assistant/message',{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({...queue[0],session_id:session?.id,token:session?.token})});
      if(!r.ok)throw new Error('Konuşma kaydedilemedi. Bağlantını kontrol edip yeniden dene.');
      queue.shift();
    }
  };
  const idRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Profil kimliği değiştiğinde (giriş/çıkış, rol değişimi, çocuk eşleşmesi) sohbeti sıfırla.
  // Böylece bir kullanıcının kişiselleştirilmiş selamlaması ya da konuşması, çıkış yaptıktan
  // sonra gelen ziyaretçiye (guest) asla sızmaz. "key" ile profil bir kare geriden geldiğinde
  // oluşan bayat selamlamayı da ortadan kaldırır.
  const profileIdentity = `${siteName}|${profile.role}|${profile.name}|${profile.childName}|${profile.childGrade}`;
  useEffect(() => {
    setMessages([{ id: "greeting", role: "assistant", text: buildGreeting(profile, siteName) }]);
    logSession.current=null;pendingLogs.current=[];setGuestName("");setGuestPhone("");setLogError("");
    setTyping(false);
    setInput("");
    setImage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileIdentity]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, typing, image]);

  const onPickImage = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !file.type.startsWith("image/")) return;
    if (file.size > 5 * 1024 * 1024) return; // 5MB sınırı
    const reader = new FileReader();
    reader.onload = () => setImage(typeof reader.result === "string" ? reader.result : null);
    reader.readAsDataURL(file);
  };

  const removeImage = () => setImage(null);

  const send = async (text: string) => {
    const clean = text.trim();
    if ((!clean && !image) || typing) return;

    if(profile.role==='guest'&&!/^\S+\s+\S+/.test(guestName.trim())){setLogError('Önce adını ve soyadını yaz.');return;}
    setTyping(true);setLogError("");
    try {
      if(!logSession.current){const r=await fetch('/api/assistant/session',{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({name:guestName.trim(),phone:guestPhone.trim()})});const d=await r.json();if(!r.ok)throw new Error(d.error||'Konuşma başlatılamadı.');logSession.current=d;}
    }catch(e){setLogError(e instanceof Error?e.message:'Konuşma başlatılamadı.');setTyping(false);return;}
    idRef.current += 1;
    const userMsg: ChatMessage = {
      id: `u-${idRef.current}`,
      role: "user",
      text: clean || "Bu soruyu çöz.",
      image: image ?? undefined,
    };
    try{await archive({id:userMsg.id,role:'user',text:userMsg.text,image_attached:!!userMsg.image});}catch(e){setLogError(String(e));setTyping(false);return;}
    const sendSession=logSession.current;
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput("");
    setImage(null);
    setTyping(true);

    // Asistan yanıtını akışkan göstermek için boş bir balon açıyoruz,
    // tokenlar geldikçe bu balon canlı olarak dolacak.
    idRef.current += 1;
    const assistantId = `a-${idRef.current}`;
    setMessages((prev) => [...prev, { id: assistantId, role: "assistant", text: "" }]);

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 45000);
    try {
      const response = await fetch('/api/assistant/chat', {
        method: 'POST', credentials: 'include', signal: controller.signal,
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({session_id: sendSession?.id, token: sendSession?.token, text: clean}),
      });
      const data = await response.json();
      if (!response.ok || typeof data.text !== 'string') throw new Error(data.error || 'Asistan yanıtı alınamadı.');
      const finalReply = data.text + (userMsg.image ? '\n\nNot: Eklediğiniz görsel bu istekte incelenmedi; yanıt yazdığınız metne dayanıyor.' : '');
      if (logSession.current === sendSession) {
        setMessages(prev => prev.map(m => m.id === assistantId ? {...m, text: finalReply} : m));
        try { await archive({id: assistantId, role: 'assistant', text: finalReply}, sendSession); }
        catch { setLogError('Yanıt alındı ancak geçmişe kaydedilemedi.'); }
      }
    } catch (error) {
      if (logSession.current === sendSession) {
        setMessages(prev => prev.filter(m => m.id !== assistantId));
        setLogError(error instanceof Error && error.name === 'AbortError' ? 'Yanıt zaman aşımına uğradı. Yeniden deneyebilirsiniz.' : error instanceof Error ? error.message : 'Bağlantı kurulamadı.');
        setInput(clean);
      }
    } finally {
      window.clearTimeout(timer);
      if (logSession.current === sendSession) setTyping(false);
    }
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    send(input);
  };

  // "Bu Konuyu Öğren" gibi bir akış, asistanı belirli bir istekle açar;
  // o isteği otomatik gönderip sohbeti hemen başlatırız.
  useEffect(() => {
    if (open && pendingPrompt) {
      clearPrompt();
      send(pendingPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingPrompt]);

  const quickPrompts = aiCoachQuickPrompts[profile.role];

  return (
    <>
      <button
        type="button"
        aria-label="Kapat"
        onClick={closeCoach}
        className={`fixed inset-0 z-[60] w-full h-full bg-background-950/40 backdrop-blur-sm cursor-default transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`${siteName} Asistan`}
        className={`fixed inset-y-0 right-0 z-[70] w-full max-w-md bg-background-50 border-l border-background-200 flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Başlık */}
        <header className="flex items-center gap-3 px-3 py-3 md:px-4 md:py-4 border-b border-background-200 bg-background-50">
          <img src={assistantAvatar} alt={`${siteName} Asistan`} className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover" />
          <div className="min-w-0 flex-1 leading-tight">
            <div className="flex items-center gap-2">
              <p className="font-heading font-semibold text-sm md:text-base text-foreground-950 truncate">{siteName} Asistan</p>
            </div>
            <p className="text-[11px] md:text-xs text-foreground-500">Platform rehberiniz ve genel soru asistanınız</p>
          </div>
          <button
            type="button"
            onClick={closeCoach}
            aria-label="Sohbeti kapat"
            className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
          >
            <i className="ri-close-line text-lg md:text-xl" aria-hidden="true"></i>
          </button>
        </header>

        <div className="px-4 py-3 space-y-2 border-b border-background-200">
          <p className="text-xs text-foreground-600">Mesajlarınız, destek amacıyla Dersdinle yönetici panelinde kaydedilir. Şifre veya ödeme bilgisi paylaşmayın. Yanıt oluşturmak için mesajlarınız ve yakın sohbet geçmişiniz Google Gemini hizmetine gönderilir. Yapay zekâ yanıtları hata içerebilir.</p>
          {profile.role==='guest'&&<><input aria-label="Ad soyad" placeholder="Ad soyad (zorunlu)" maxLength={100} value={guestName} disabled={!!logSession.current} onChange={e=>setGuestName(e.target.value)} className="w-full rounded-lg border p-2"/><input aria-label="Telefon (isteğe bağlı)" placeholder="Telefon (isteğe bağlı)" type="tel" maxLength={25} value={guestPhone} disabled={!!logSession.current} onChange={e=>setGuestPhone(e.target.value)} className="w-full rounded-lg border p-2"/></>}
          {logError&&<p role="alert" className="text-sm text-red-600">{logError}</p>}
        </div>
        {/* Kişisel bağlam şeridi */}
        <div className="px-3 md:px-4 pt-2 md:pt-3">
          <div className="flex items-center gap-2 rounded-lg bg-accent-50 border border-accent-100 px-3 py-2">
            <i className="ri-sparkling-2-line text-accent-600 text-base" aria-hidden="true"></i>
            <span className="text-[11px] md:text-xs text-accent-900 truncate">{contextLabel(profile)}</span>
          </div>
        </div>

        {/* Mesajlar */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 md:px-4 py-3 md:py-4 space-y-3">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[92%] sm:max-w-[85%] rounded-lg px-3 py-2 md:px-3.5 md:py-2.5 text-xs md:text-sm animate-fade-up ${
                  m.role === "user"
                    ? "bg-accent-500 text-background-50 rounded-br-sm"
                    : "bg-background-100 text-foreground-900 border border-background-200 rounded-bl-sm"
                }`}
              >
                {m.image && (
                  <img
                    src={m.image}
                    alt="Yüklenen soru görseli"
                    className="mb-2 rounded-md w-full max-w-[220px] object-cover border border-background-200"
                  />
                )}
                {m.role === "assistant" ? <MarkdownText text={m.text} onNavigate={(url) => { closeCoach(); navigate(url); }} /> : m.text}
              </div>
            </div>
          ))}

          {typing && (
            <div className="flex justify-start">
              <div className="flex items-center gap-1.5 rounded-lg rounded-bl-sm bg-background-100 border border-background-200 px-3 py-2 md:px-4 md:py-3">
                <span className="w-2 h-2 rounded-full bg-foreground-300 animate-bounce" style={{ animationDelay: "0ms" }}></span>
                <span className="w-2 h-2 rounded-full bg-foreground-300 animate-bounce" style={{ animationDelay: "150ms" }}></span>
                <span className="w-2 h-2 rounded-full bg-foreground-300 animate-bounce" style={{ animationDelay: "300ms" }}></span>
              </div>
            </div>
          )}
        </div>

        {/* Hızlı öneriler */}
        {messages.length <= 1 && !typing && (
          <div className="px-3 md:px-4 pb-2 flex flex-wrap gap-2">
            {quickPrompts.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => send(p)}
                className="whitespace-nowrap text-[11px] md:text-xs text-foreground-700 bg-background-100 hover:bg-accent-50 hover:text-accent-800 border border-background-200 rounded-full px-2.5 py-1 md:px-3 md:py-1.5 transition-colors"
              >
                {p}
              </button>
            ))}
          </div>
        )}

        {/* Giriş */}
        <form onSubmit={onSubmit} className="p-3 md:p-4 border-t border-background-200 bg-background-50">
          {image && (
            <div className="mb-2 flex items-start gap-2">
              <div className="relative">
                <img
                  src={image}
                  alt="Yüklenen görsel önizlemesi"
                  className="w-16 h-16 md:w-20 md:h-20 rounded-md object-cover border border-background-200"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  aria-label="Görseli kaldır"
                  className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center rounded-full bg-foreground-800 text-background-50 hover:bg-foreground-950 transition-colors"
                >
                  <i className="ri-close-line text-xs" aria-hidden="true"></i>
                </button>
              </div>
              <p className="text-[11px] text-foreground-500 leading-tight">
                Soru görseli eklendi. Gönderdiğinde asistan görseldeki soruyu çözümleyecek.
              </p>
            </div>
          )}

          <div className="flex items-end gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={`${siteName} Asistan'a sor...`}
              className="flex-1 min-w-0 px-3 py-2 md:px-3.5 md:py-2.5 rounded-md border border-background-200 bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:bg-background-50 transition-colors"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickImage}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              aria-label="Soru fotoğrafı yükle"
              className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-md border border-background-200 bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 transition-colors"
            >
              <i className="ri-image-add-line text-base md:text-lg" aria-hidden="true"></i>
            </button>
            <button
              type="submit"
              disabled={(!input.trim() && !image) || typing}
              aria-label="Gönder"
              className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-md bg-accent-500 text-background-50 hover:bg-accent-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-send-plane-2-fill text-base md:text-lg" aria-hidden="true"></i>
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}